import { Description, Field, Label, Radio, RadioGroup } from '@headlessui/react'
import { PlusIcon } from '@heroicons/react/24/outline'

import Button from '@/components/TailwindUI/Button'
import { WorkgridCatalogDatasource, WorkgridDatasource } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import cn from '@/utilities/cn'

type DataSourceSwitcherProps = {
  value?: WorkgridDatasource
  dataSourceConfiguration: WorkgridCatalogDatasource
  dataSources: WorkgridDatasource[]
  onChange?: any
  onAddNewInstance: () => void
  isWorkgridDataSource: boolean
  errorMessage?: string
}

export default function WorkgridDataSourceSwitcher({
  value,
  onChange,
  dataSources,
  dataSourceConfiguration,
  onAddNewInstance,
  errorMessage
}: DataSourceSwitcherProps) {
  return (
    <div className="overflow-auto">
      <div className="mb-4 mt-2 text-zinc-900">
        Select a saved instance of this data source or create a new instance
      </div>
      <RadioGroup value={value || null} onChange={onChange}>
        <Label className="sr-only">Data Source Instance</Label>
        <div className="space-y-3">
          {dataSources.map(ds => (
            <Field key={ds.id}>
              <Radio
                as="div"
                value={ds}
                className={({ checked }) =>
                  cn(
                    checked ? 'z-10 border-blue-700' : 'cursor-pointer border-zinc-300 hover:border-zinc-400',
                    'relative rounded-xl border-2 bg-white p-4 focus:outline-none'
                  )
                }
              >
                {({ focus, checked }) => (
                  <div className="flex items-center justify-between gap-x-4 pr-2">
                    <div className="flex flex-none">
                      <img
                        src={dataSourceConfiguration?.iconUrl}
                        className="h-12 w-12 rounded-md"
                        alt="data source instance icon"
                      />
                    </div>
                    <div className="flex w-full flex-col">
                      <Label className={cn('text-sm text-zinc-500', { 'cursor-pointer': !checked })}>
                        {dataSourceConfiguration.name}
                      </Label>
                      <Description className="text-lg font-semibold text-zinc-900">{ds.name}</Description>
                    </div>
                    <div className="flex justify-end">
                      <div
                        className={cn(
                          checked ? 'border-transparent bg-blue-700' : 'cursor-pointer border-zinc-300 bg-white',
                          focus ? 'ring-2 ring-blue-700 ring-offset-2' : '',
                          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2'
                        )}
                        aria-hidden="true"
                      >
                        <span className="h-2 w-2 rounded-full bg-white" />
                      </div>
                    </div>
                  </div>
                )}
              </Radio>
            </Field>
          ))}
        </div>
      </RadioGroup>
      {errorMessage && <p className="mt-4 text-base font-semibold text-red-600">{errorMessage}</p>}
      <Button
        className="mt-6 ml-1 flex items-center gap-x-1 text-base font-semibold text-blue-700 hover:text-blue-500"
        onClick={onAddNewInstance}
      >
        <PlusIcon className="h-4 w-4 stroke-2" />
        <span>New instance</span>
      </Button>
    </div>
  )
}
